import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Re-usable checklist customizer used under the workout + meal "Generate" buttons.
 *
 * Props:
 *   options: [{ id, label, kind: 'check' | 'select' | 'text', selectOptions?, placeholder?, requiresInput? }]
 *   values: { [id]: bool | string }
 *   onChange: (id, value) => void
 *   testIdPrefix: e.g. "workout-opts"
 *
 * Behaviour: When a `check` option with `requiresInput: true` is ticked, a small
 * text input appears below it for follow-up detail.
 */
export default function CustomizerChecklist({ title = "Customize", options, values, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  // An option counts as "active" if it has a meaningful value. For check-with-
  // requiresInput, the value becomes "" (empty string) the moment the user
  // ticks the box — which is intentionally truthy-as-state but falsy as bool,
  // so we have to detect strings explicitly here AND on the checked indicator
  // below. Without this, ticking the box flips the underlying value to "" and
  // the box would immediately appear unchecked, hiding the follow-up input.
  const activeCount = options.filter((o) => {
    const v = values[o.id];
    if (o.kind === "check" && o.requiresInput) return typeof v === "string";
    return !!v;
  }).length;

  return (
    <div className="border border-zinc-800 bg-[#0F0F0F]" data-testid={`${testIdPrefix}-customizer`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#141414] transition-colors"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-[#E5B43A]" /> : <ChevronRight size={14} className="text-[#E5B43A]" />}
          <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-300">{title}</span>
          {activeCount > 0 && (
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#E5B43A] border border-[#E5B43A]/40 px-1.5 py-0.5">{activeCount} set</span>
          )}
        </div>
        <span className="text-[10px] tracking-widest uppercase text-zinc-500">{open ? "hide" : "options"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-900">
          {options.map((opt) => {
            const value = values[opt.id];
            if (opt.kind === "select") {
              return (
                <div key={opt.id}>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">{opt.label}</label>
                  <select
                    value={value || ""}
                    onChange={(e) => onChange(opt.id, e.target.value)}
                    data-testid={`${testIdPrefix}-${opt.id}`}
                    className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E5B43A]"
                  >
                    <option value="">— pick one —</option>
                    {opt.selectOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              );
            }
            if (opt.kind === "text") {
              return (
                <div key={opt.id}>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">{opt.label}</label>
                  <input
                    type={opt.inputType || "text"}
                    value={value || ""}
                    onChange={(e) => onChange(opt.id, e.target.value)}
                    placeholder={opt.placeholder}
                    data-testid={`${testIdPrefix}-${opt.id}`}
                    className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E5B43A]"
                  />
                </div>
              );
            }
            // check (with optional follow-up text)
            // For requiresInput, the value is "" once checked but holds the
            // typed text after — both are "checked" states. Detect via type
            // so the box doesn't appear to instantly uncheck itself.
            const checked = opt.requiresInput ? typeof value === "string" : !!value;
            return (
              <div key={opt.id}>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(opt.id, e.target.checked ? (opt.requiresInput ? "" : true) : false)}
                    data-testid={`${testIdPrefix}-${opt.id}-check`}
                    className="mt-1 w-4 h-4 accent-[#E5B43A] shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-200">{opt.label}</div>
                    {opt.hint && <div className="text-[10px] text-zinc-500 mt-0.5">{opt.hint}</div>}
                  </div>
                </label>
                {checked && opt.requiresInput && (
                  <input
                    type="text"
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => onChange(opt.id, e.target.value)}
                    placeholder={opt.placeholder}
                    data-testid={`${testIdPrefix}-${opt.id}-input`}
                    className="mt-2 w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E5B43A]"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
